// src/hooks/menu/useMenuNavigation.js
import { useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import { toLowerWithDashes } from '../../util';
import { filterTypes } from '../../config';

export const useMenuNavigation = ({
    selectedFilters,
    setSelectedFilters,
    menuFilter,
    filterTerms,
    specialBrands,
    selectedSpecialId,
    searchQuery
}) => {
    const params = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    
    // Use refs to track the last navigation to prevent loops
    const lastNavigatedPath = useRef(location.pathname);
    const isNavigating = useRef(false);

    // Navigate based on category filter
    const categoryNavigate = useCallback(() => {
        if (!selectedFilters || isNavigating.current) return;
        
        if (params.categoryType === 'brand' && selectedFilters[filterTypes.brands]?.length > 0) {
            return;
        }
        
        if (selectedFilters.specials && specialBrands && selectedFilters.brandIds?.length > 0) {
            const filteredSpecialBrands = specialBrands
                .filter((brand) => selectedFilters.brandIds.includes(brand.id))
                .map((brand) => brand.id);
                
            if (!isEqual(filteredSpecialBrands, selectedFilters.brandIds)) {
                setSelectedFilters(sf => ({ ...sf, brandIds: filteredSpecialBrands }));
            }
            return;
        }
        
        let path = `/shop/${params.locationSlug}/`;
        if (selectedFilters.specials) {
            path += 'specials';
            if (selectedFilters[filterTypes.category]) {
                path += `/category/${toLowerWithDashes(selectedFilters[filterTypes.category])}`;
            }
        } else if (selectedFilters[filterTypes.category]) {
            path += `category/${toLowerWithDashes(selectedFilters[filterTypes.category])}`;
        }
        
        // Only navigate if the path is different
        if (path !== location.pathname) {
            isNavigating.current = true;
            lastNavigatedPath.current = path;
            navigate(path, { state: { setFilters: false } });
            setTimeout(() => { isNavigating.current = false; }, 100);
        }
    }, [selectedFilters, params.locationSlug, params.categoryType, specialBrands, navigate, setSelectedFilters, location.pathname]);

    // Navigate based on brand filter
    const brandsNavigate = useCallback(() => {
        if (!selectedFilters || isNavigating.current) return;
        
        if (selectedFilters.specials && specialBrands && selectedFilters.brandIds.length > 1) {
            const filteredSpecialBrands = specialBrands
                .filter((brand) => selectedFilters.brandIds.includes(brand.id))
                .map((brand) => brand.id);
            
            const path = `/shop/${params.locationSlug}/specials`;
            
            if (path !== location.pathname) {
                isNavigating.current = true;
                lastNavigatedPath.current = path;
                navigate(path, { state: { setFilters: false } });
                setTimeout(() => { isNavigating.current = false; }, 100);
            }
            
            if (!isEqual(filteredSpecialBrands, selectedFilters.brandIds)) {
                setSelectedFilters({ ...menuFilter, brandIds: filteredSpecialBrands, specials: true });
            }
            return;
        }
        
        const brandName = filterTerms.brands?.find(b => 
            selectedFilters.brandIds && selectedFilters.brandIds[0] === b.id
        )?.name;
        
        if (!brandName) {
            if (selectedFilters.category) {
                categoryNavigate();
            }
            return;
        }
        
        const path = `/shop/${params.locationSlug}/${
            selectedFilters.specials ? 'specials' : 'brand'
        }/${
            selectedFilters.specials ? 'brand/' + toLowerWithDashes(brandName) : toLowerWithDashes(brandName)
        }/`;
        
        // Only navigate if the path is different
        if (path !== location.pathname) {
            isNavigating.current = true;
            lastNavigatedPath.current = path;
            navigate(path, { state: { setFilters: false } });
            setTimeout(() => { isNavigating.current = false; }, 100);
        }
    }, [selectedFilters, specialBrands, menuFilter, params.locationSlug, filterTerms?.brands, navigate, setSelectedFilters, categoryNavigate, location.pathname]);

    // Update URL when filters change - with proper guards
    useEffect(() => {
        // Skip if we're already navigating
        if (isNavigating.current) {
            return;
        }

        // Add a debounce to prevent rapid-fire navigations
        const navigationTimeout = setTimeout(() => {
            // IMPORTANT: Check if we should navigate to base URL (when filters match menuFilter)
            const filtersMatchBase = JSON.stringify(selectedFilters) === JSON.stringify(menuFilter);
            const currentPath = location.pathname;
            const basePath = `/shop/${params.locationSlug}/`;
            const basePathNoSlash = `/shop/${params.locationSlug}`;
            
            // If filters were cleared (match base) but we're not at base URL, navigate to base
            if (filtersMatchBase && currentPath !== basePath && currentPath !== basePathNoSlash) {
                isNavigating.current = true;
                lastNavigatedPath.current = basePath;
                navigate(basePath, { state: { setFilters: false } });
                setTimeout(() => { isNavigating.current = false; }, 100);
                return;
            }

            // Handle specific filter navigations
            if (selectedFilters[filterTypes.category] || selectedFilters[filterTypes.brands]?.length > 0) {
                if (params?.categoryType) {
                    if (
                        (params.categoryType === 'brand' || 
                         (params.categoryType === 'effect' && selectedFilters[filterTypes.effects]?.length === 0) || 
                         params.categoryType === 'specials' || 
                         params.categoryType === 'special') &&
                        selectedFilters[filterTypes.brands]
                    ) {
                        brandsNavigate();
                    } else if (
                        (params.categoryType === 'category' || 
                         (params.categoryType === 'effect' && selectedFilters[filterTypes.effects]?.length === 0) || 
                         params.categoryType === 'specials' || 
                         params.categoryType === 'special') &&
                        selectedFilters[filterTypes.category]
                    ) {
                        categoryNavigate();
                    } else if (params.categoryType === 'brand' && !(selectedFilters[filterTypes.brands]?.length > 0)) {
                        if (selectedFilters[filterTypes.category]) {
                            categoryNavigate();
                        } else {
                            const path = `/shop/${params.locationSlug}/`;
                            if (path !== location.pathname) {
                                isNavigating.current = true;
                                lastNavigatedPath.current = path;
                                navigate(path, { state: { setFilters: false } });
                                setTimeout(() => { isNavigating.current = false; }, 100);
                            }
                        }
                    } else if (params.categoryType === 'category' && !selectedFilters[filterTypes.category]) {
                        if (selectedFilters[filterTypes.brands]?.length > 0) {
                            brandsNavigate();
                        } else {
                            const path = `/shop/${params.locationSlug}/`;
                            if (path !== location.pathname) {
                                isNavigating.current = true;
                                lastNavigatedPath.current = path;
                                navigate(path, { state: { setFilters: false } });
                                setTimeout(() => { isNavigating.current = false; }, 100);
                            }
                        }
                    }
                } else {
                    if (selectedFilters[filterTypes.category]) {
                        categoryNavigate();
                    } else if (selectedFilters[filterTypes.brands]?.length > 0) {
                        brandsNavigate();
                    }
                }
            } else if (selectedFilters[filterTypes.specials]) {
                if (selectedSpecialId) {
                    const path = `/shop/${params.locationSlug}/special/${selectedSpecialId}/`;
                    if (path !== location.pathname) {
                        isNavigating.current = true;
                        lastNavigatedPath.current = path;
                        navigate(path, { state: { setFilters: false } });
                        setTimeout(() => { isNavigating.current = false; }, 100);
                    }
                } else {
                    if (params?.categoryType !== 'special') {
                        categoryNavigate();
                    }
                }
            } else if (selectedFilters[filterTypes.effects]?.length > 0) {
                const path = `/shop/${params.locationSlug}/effect/${selectedFilters[filterTypes.effects][0].toLowerCase()}/`;
                if (path !== location.pathname) {
                    isNavigating.current = true;
                    lastNavigatedPath.current = path;
                    navigate(path, { state: { setFilters: false } });
                    setTimeout(() => { isNavigating.current = false; }, 100);
                }
            } else if (selectedFilters[filterTypes.subcategory]) {
                const path = `/shop/${params.locationSlug}/subcategory/${selectedFilters[filterTypes.subcategory].toLowerCase()}/`;
                if (path !== location.pathname) {
                    isNavigating.current = true;
                    lastNavigatedPath.current = path;
                    navigate(path, { state: { setFilters: false } });
                    setTimeout(() => { isNavigating.current = false; }, 100);
                }
            } else if (selectedFilters[filterTypes.tags]?.length > 0) {
                const path = `/shop/${params.locationSlug}/tags/${encodeURIComponent(selectedFilters[filterTypes.tags][0])}/`;
                if (path !== location.pathname) {
                    isNavigating.current = true;
                    lastNavigatedPath.current = path;
                    navigate(path, { state: { setFilters: false } });
                    setTimeout(() => { isNavigating.current = false; }, 100);
                }
            } else {
                // If no filters are set and we're not at base URL, navigate to base
                if (params?.categoryType !== 'special' && 
                    !searchQuery && 
                    currentPath !== basePath && 
                    currentPath !== basePathNoSlash) {
                    isNavigating.current = true;
                    lastNavigatedPath.current = basePath;
                    navigate(basePath, { state: { setFilters: false } });
                    setTimeout(() => { isNavigating.current = false; }, 100);
                }
            }
        }, 100); // 100ms debounce

        return () => clearTimeout(navigationTimeout);
    }, [selectedSpecialId, selectedFilters, params, location.pathname, searchQuery, menuFilter]); // Added menuFilter to dependencies

    return {
        categoryNavigate,
        brandsNavigate
    };
};
